/**
 * スケジュール関連ユーティリティ
 * ScheduleTable.tsx / Scheduling.tsx / ReportAndDraft.tsx / generatePdf.ts の
 * 週計算・月リスト計算ロジックを一元管理する
 */
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import type { ScheduleItem, FacilityItem, ReviewScheduleItem } from '../contexts/types';

dayjs.extend(isoWeek);

/**
 * 日付文字列(YYYY-MM-DD)を Date に変換する
 * @param dateString 日付文字列
 * @returns Date オブジェクト。無効な場合は null
 */
export const parseScheduleDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  const d = new Date(dateString);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * 月の週リスト（ISO週・月曜始まり）を返す
 * 月をまたぐ場合は月初・月末でクリップする
 * 5週に満たない場合は月末日で埋める
 * @param year 年
 * @param month 月（1始まり）
 * @returns 週の開始日・終了日の配列（最大5要素）
 */
export const getMonthWeeks = (year: number, month: number): { start: Date; end: Date }[] => {
  const weeks: { start: Date; end: Date }[] = [];
  const startOfMonth = dayjs(new Date(year, month - 1, 1));
  const endOfMonth = dayjs(new Date(year, month, 0));

  let current = startOfMonth.startOf('isoWeek');
  while (current.isBefore(endOfMonth) || current.isSame(endOfMonth, 'day')) {
    let startOfWeek = current.startOf('isoWeek');
    let endOfWeek = current.endOf('isoWeek');

    // 週の開始日が月の開始日より前の場合、月の開始日に設定
    if (startOfWeek.isBefore(startOfMonth)) {
      startOfWeek = startOfMonth;
    }

    // 週の終了日が月の終了日より後の場合、月の終了日に設定
    if (endOfWeek.isAfter(endOfMonth)) {
      endOfWeek = endOfMonth;
    }

    weeks.push({
      start: startOfWeek.toDate(),
      end: endOfWeek.toDate(),
    });

    current = current.add(1, 'week');
  }

  // 5週に満たない場合、残りを月末日で埋める
  while (weeks.length < 5) {
    weeks.push({
      start: endOfMonth.toDate(),
      end: endOfMonth.toDate(),
    });
  }
  return weeks;
};

/**
 * 日付が指定月の第何週に属するかを返す（1始まり）
 * @param date 日付文字列
 * @returns 第N週（1始まり）
 */
export const getWeekOfMonth = (date: string): number => {
  const dayjsDate = dayjs(date);
  const startOfMonth = dayjsDate.startOf('month');
  const startIsoWeek = startOfMonth.isoWeek();
  const currentIsoWeek = dayjsDate.isoWeek();
  return currentIsoWeek - startIsoWeek + 1;
};

/**
 * 週範囲(week)が指定期間(start〜end)に重なるかを判定する
 * @param week 週の開始日・終了日
 * @param start 期間開始日文字列
 * @param end 期間終了日文字列
 * @returns 重なる場合 true
 */
export const isWithinWeekRange = (
  week: { start: Date; end: Date },
  start: string,
  end: string
): boolean => {
  const startDate = dayjs(start);
  const endDate = dayjs(end);
  if (!startDate.isValid() || !endDate.isValid()) return false;
  const weekStart = dayjs(week.start);
  const weekEnd = dayjs(week.end);

  // 週初〜週末 と 期間開始〜終了 が1日でも重なれば true
  return !weekStart.isAfter(endDate) && !weekEnd.isBefore(startDate);
};

/**
 * 顧客スケジュール日付が指定月の指定週インデックスに含まれるかを判定する
 * @param date 日付文字列
 * @param weekIndex 週インデックス（0始まり）
 * @param year 年
 * @param month 月（1始まり）
 * @returns 含まれる場合 true
 */
export const isScheduleDateInWeek = (
  date: string,
  weekIndex: number,
  year: number,
  month: number
): boolean => {
  const d = parseScheduleDate(date);
  if (!d) return false;
  if (d.getFullYear() !== year || d.getMonth() !== month - 1) return false;
  const weeks = getMonthWeeks(year, month);
  if (weekIndex >= weeks.length) return false;
  const week = weeks[weekIndex];
  return d >= week.start && d <= week.end;
};

/**
 * 月を n か月進める
 * @param date 基準日
 * @param months 進める月数
 * @returns n か月後の Date
 */
export const addMonths = (date: Date, months: number): Date => {
  const d = new Date(date.getFullYear(), date.getMonth() + months, 1);
  return d;
};

/**
 * 月を n か月戻す
 * @param date 基準日
 * @param months 戻す月数
 * @returns n か月前の Date
 */
export const subMonths = (date: Date, months: number): Date => {
  return addMonths(date, -months);
};

/** 年月を "YYYY-MM" 形式にする（内部ユーティリティ） */
const toYearMonthString = (date: Date): string => {
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  return `${y}-${String(m).padStart(2, '0')}`;
};

/**
 * start〜end の範囲を "YYYY-MM" 形式の配列で返す
 * 日を1日に正規化してループ（月末日でsetMonthするとオーバーフローし月がスキップされるため）
 * @param start 開始日
 * @param end 終了日
 * @returns "YYYY-MM" 配列
 */
export const generateMonthRange = (start: Date, end: Date): string[] => {
  const range: string[] = [];
  let current = new Date(start.getFullYear(), start.getMonth(), 1);
  const endNorm = new Date(end.getFullYear(), end.getMonth(), 1);
  while (current.getTime() <= endNorm.getTime()) {
    range.push(toYearMonthString(current));
    current.setMonth(current.getMonth() + 1);
  }
  return range;
};

/**
 * "YYYY-MM" の配列を年ごとにグループ化する
 * 例: ["2025-11","2025-12","2026-01"] → [{ year:"2025", months:["2025-11","2025-12"] }, ...]
 * @param monthList "YYYY-MM" 配列
 * @returns 年ごとのグループ配列
 */
export const groupByYear = (monthList: string[]): { year: string; months: string[] }[] => {
  const result: { year: string; months: string[] }[] = [];
  monthList.forEach((ym) => {
    const [year] = ym.split('-');
    const existing = result.find((r) => r.year === year);
    if (existing) {
      existing.months.push(ym);
    } else {
      result.push({ year, months: [ym] });
    }
  });
  return result;
};

/**
 * 月（"YYYY-MM"）が指定期間（startDate〜endDate、YYYY-MM-DD形式）に重なるかを判定する
 * 月の初日〜月末日と期間が1日でも重なれば true
 * @param ym 判定対象月 "YYYY-MM"
 * @param startDate 期間開始 "YYYY-MM-DD"
 * @param endDate 期間終了 "YYYY-MM-DD"
 * @returns 重なる場合 true
 */
export const isWithinMonthRange = (ym: string, startDate: string, endDate: string): boolean => {
  if (!startDate || !endDate) return false;
  const monthStart = dayjs(`${ym}-01`);       // dayjs は "YYYY-MM-DD" をローカルタイムとしてパース
  const monthEnd = monthStart.endOf('month'); // 月末日をローカル基準で取得
  const periodStart = dayjs(startDate);
  const periodEnd = dayjs(endDate);

  // 月初〜月末 と 期間開始〜終了 が1日でも重なれば true
  return !monthStart.isAfter(periodEnd) && !monthEnd.isBefore(periodStart);
};

/**
 * 顧客・設備投資・振り返りスケジュールから monthList を計算する
 * 最小日付の1か月前〜最大日付の1か月後を返す
 * @param scheduleItems 顧客スケジュール
 * @param facilityItems 設備投資スケジュール
 * @param reviewItems 振り返りスケジュール
 * @returns "YYYY-MM" 配列（空データの場合は空配列）
 */
export const calcMonthList = (
  scheduleItems: ScheduleItem[],
  facilityItems: FacilityItem[],
  reviewItems: ReviewScheduleItem[]
): string[] => {
  const custDates = scheduleItems
    .map((si) => parseScheduleDate(si.date))
    .filter((d): d is Date => d !== null);

  const facilityDates = facilityItems
    .flatMap((fi) => {
      const start = parseScheduleDate(fi.startDate);
      const end = parseScheduleDate(fi.endDate);
      return [start, end];
    })
    .filter((d): d is Date => d !== null);

  const reviewDates = reviewItems
    .map((ri) => parseScheduleDate(ri.date))
    .filter((d): d is Date => d !== null);

  const allDates = [...custDates, ...facilityDates, ...reviewDates];
  if (allDates.length === 0) return [];

  let earliest = new Date(Math.min(...allDates.map((d) => d.getTime())));
  let latest = new Date(Math.max(...allDates.map((d) => d.getTime())));

  earliest = subMonths(earliest, 1);
  latest = addMonths(latest, 1);

  return generateMonthRange(earliest, latest);
};
